import { createAsyncThunk } from '@reduxjs/toolkit';
import { formulaMateService } from '../formulaMateService';
import type { FormulaMateResponse } from '../formulaMate.types';
import type { FormulaApiState } from '../types/formulaApi.types';

type ThunkConfig = {
  state: { formulaApi: FormulaApiState };
  rejectValue: string;
};

const idle = (
  _: unknown,
  { getState }: { getState: () => ThunkConfig['state'] }
) => getState().formulaApi.status === 'idle';

const failure = (error: unknown) =>
  error instanceof Error
    ? error.message
    : 'The FormulaMate AI request failed. Please try again.';

export const createFormulaSession = createAsyncThunk<string, void, ThunkConfig>(
  'formulaApi/createSession',
  async (_, { signal, rejectWithValue }) => {
    try {
      return await formulaMateService.createSession(signal);
    } catch (error) {
      return rejectWithValue(failure(error));
    }
  },
  {
    condition: (_, api) => idle(_, api) && !api.getState().formulaApi.sessionId,
  }
);

export const generateFormula = createAsyncThunk<
  FormulaMateResponse,
  { sessionId: string; prompt: string },
  ThunkConfig
>(
  'formulaApi/generateFormula',
  async ({ sessionId, prompt }, { signal, rejectWithValue }) => {
    try {
      return await formulaMateService.generateFormula(
        sessionId,
        prompt,
        signal
      );
    } catch (error) {
      return rejectWithValue(failure(error));
    }
  },
  { condition: idle }
);

export const reviewFormula = createAsyncThunk<
  FormulaMateResponse,
  { sessionId: string; code: string },
  ThunkConfig
>(
  'formulaApi/reviewFormula',
  async ({ sessionId, code }, { signal, rejectWithValue }) => {
    console.log(
      'Reviewing formula with sessionId:',
      sessionId,
      'and code:',
      code
    );
    try {
      return await formulaMateService.reviewCode(sessionId, code, '', signal);
    } catch (error) {
      return rejectWithValue(failure(error));
    }
  },
  { condition: idle }
);

export const deleteFormulaSession = createAsyncThunk<void, string, ThunkConfig>(
  'formulaApi/deleteSession',
  async (sessionId, { signal, rejectWithValue }) => {
    try {
      await formulaMateService.endSession(sessionId, signal);
    } catch (error) {
      return rejectWithValue(failure(error));
    }
  },
  { condition: idle }
);
